import { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Copy, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/utils';

interface ErrorDisplayProps {
  error: Error | string | null;
  context?: string;
  onRetry?: () => void;
  className?: string;
}

interface ErrorDetails {
  errorType: string;
  message: string;
  timestamp: string;
  errorCode?: string;
  suggestedAction?: string;
  technicalDetails?: Record<string, any>;
}

export function ErrorDisplay({ error, context, onRetry, className }: ErrorDisplayProps) {
  const [expanded, setExpanded] = useState(false);

  if (!error) return null;

  const errorDetails: ErrorDetails = {
    errorType: context || 'Application Error',
    message: error instanceof Error ? error.message : error,
    timestamp: new Date().toISOString(),
    errorCode: error instanceof Error ? error.name : 'UNKNOWN_ERROR',
    suggestedAction: getSuggestedAction(error),
    technicalDetails: error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause
    } : { rawError: error }
  };

  const copyErrorDetails = () => {
    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
  };

  return (
    <div className={cn(
      "bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg overflow-hidden",
      className
    )}>
      {/* Error Summary */}
      <div className="p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="ml-3 flex-1 min-w-0">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
              {errorDetails.errorType}
            </h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              <p className="whitespace-pre-wrap break-words">
                {errorDetails.message}
              </p>
            </div>
          </div>
          <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
            {onRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-red-200 dark:border-red-800 bg-red-100/50 dark:bg-red-900/25 p-4">
          <div className="space-y-4">
            {/* Timestamp */}
            <div>
              <h4 className="text-xs font-medium text-red-800 dark:text-red-300 uppercase">
                Timestamp
              </h4>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {new Date(errorDetails.timestamp).toLocaleString()}
              </p>
            </div>

            {/* Error Code */}
            <div>
              <h4 className="text-xs font-medium text-red-800 dark:text-red-300 uppercase">
                Error Code
              </h4>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {errorDetails.errorCode}
              </p>
            </div>

            {/* Suggested Action */}
            {errorDetails.suggestedAction && (
              <div>
                <h4 className="text-xs font-medium text-red-800 dark:text-red-300 uppercase">
                  Suggested Action
                </h4>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {errorDetails.suggestedAction}
                </p>
              </div>
            )}

            {/* Technical Details */}
            <div>
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-red-800 dark:text-red-300 uppercase">
                  Technical Details
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyErrorDetails}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="mt-2 p-2 bg-red-100 dark:bg-red-900/50 rounded text-xs text-red-700 dark:text-red-300 overflow-x-auto">
                {JSON.stringify(errorDetails.technicalDetails, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getSuggestedAction(error: Error | string): string {
  const errorStr = error instanceof Error ? error.message : error;
  
  if (errorStr.includes('duplicate key value')) {
    return 'A summary already exists for this survey question. Please try refreshing the page to see the latest summary.';
  }
  
  if (errorStr.includes('permission denied')) {
    return 'You may not have the required permissions. Please check your access level or contact an administrator.';
  }
  
  if (errorStr.includes('network')) {
    return 'Please check your internet connection and try again.';
  }
  
  if (errorStr.includes('timeout')) {
    return 'The request took too long to complete. Please try again.';
  }
  
  if (errorStr.includes('not found')) {
    return 'The requested resource could not be found. Please verify the URL or contact support.';
  }
  
  return 'Please try again or contact support if the problem persists.';
}